import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '@spartalabs/pdc-core';
import { fetchFinesGeoNotifications } from 'pages/Multas/repositories/finesGeo.repository';
import { hasPortalPermission } from 'utils/hasPortalPermission';
import { mapFinesToBellNotifications } from 'utils/mapFinesToBellNotifications';
import {
  markFineNotificationViewed,
  readViewedFineNotificationIds,
} from 'utils/multasFineNotificationsViewed';

export function useFinesBellNotifications(isOpen: boolean) {
  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );
  const currentGroupId = useAppSelector(
    state => state.authSlicer.user.currentGroupId,
  );
  const userKey = useAppSelector(state => state.authSlicer.user.cpf);
  const sessionAnchor = useAppSelector(
    state => state.authSlicer.user.ultimoAcessoAnterior,
  );

  const canLoad =
    isOpen &&
    hasPortalPermission(permissions, 'finesPortal') &&
    Boolean(currentGroupId);

  const [items, setItems] = useState<notificationCentral>([]);
  const [viewedIds, setViewedIds] = useState<Set<string>>(() =>
    readViewedFineNotificationIds(userKey, sessionAnchor),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const reloadViewed = useCallback(() => {
    setViewedIds(readViewedFineNotificationIds(userKey, sessionAnchor));
  }, [userKey, sessionAnchor]);

  useEffect(() => {
    reloadViewed();
  }, [reloadViewed, userKey, sessionAnchor]);

  useEffect(() => {
    if (!canLoad) {
      setItems([]);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    fetchFinesGeoNotifications(currentGroupId)
      .then(fines => {
        if (cancelled) return;
        setItems(mapFinesToBellNotifications(fines));
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setIsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canLoad, currentGroupId]);

  const visibleItems = items.filter(item => !viewedIds.has(item.id));

  const markViewed = useCallback(
    (fineNotificationId: string) => {
      markFineNotificationViewed(userKey, fineNotificationId, sessionAnchor);
      setViewedIds(prev => {
        const next = new Set(prev);
        next.add(fineNotificationId);
        return next;
      });
    },
    [userKey, sessionAnchor],
  );

  return {
    items: visibleItems,
    unreadCount: visibleItems.length,
    isLoading,
    isError,
    markViewed,
    refetch: () => {
      if (!canLoad) return;
      setIsLoading(true);
      setIsError(false);
      fetchFinesGeoNotifications(currentGroupId)
        .then(fines => setItems(mapFinesToBellNotifications(fines)))
        .catch(() => {
          setItems([]);
          setIsError(true);
        })
        .finally(() => setIsLoading(false));
    },
  };
}
